/* tslint:disable */
/* eslint-disable */
/* Mocked in-memory implementation of ItemsClient for local development and UI wiring. */

import { CancelToken } from 'axios';
import {
  SuccessResponse_1OfGetUomsResponse,
  SuccessResponse_1OfList_1OfItem,
  SuccessResponse_1OfGetItemsResponse,
  GetItemsByIdsRequest,
  GetItemsByStoresRequest,
  Item,
  SuccessResponse_1OfItem,
  RefreshItemsRequest,
  PagedData_1OfItemFeedChange,
  PagedData_1OfGroupedItemFeedChange,
  SuccessResponse_1OfList_1OfItemAudit,
  ItemCompare,
  GetRecipeChangesByIDRequest,
  ItemFeedChange,
  ItemStockOnHandRequest,
  ItemStockOnHandResponse,
} from '../../types';

/* ---- Helper Functions ---- */

function delay(ms: number = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function successResponse<T>(data: T): { data: T; success: boolean; message: string } {
  return { data, success: true, message: 'Success' };
}

/** Returns the item prefix for a given store number to scope items by franchise */
function getItemPrefixForStore(storeNumber?: string): string | null {
  if (!storeNumber) return null;
  if (storeNumber.startsWith('VAP')) return 'VAP-';
  if (storeNumber.startsWith('FG')) return 'FG-';
  return 'SKU-';
}

/** Filters items array to only include items belonging to the store's franchise */
function filterItemsByStore(items: Item[], storeNumber?: string): Item[] {
  const prefix = getItemPrefixForStore(storeNumber);
  if (!prefix) return items;
  return items.filter((i) => i.itemNumber?.startsWith(prefix));
}

/* ---- Mock Data ---- */

const mockItems: Item[] = [
  {
    id: 101,
    itemNumber: 'SKU-MILK-001',
    description: 'Whole Milk 2L',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Dairy',
    costPrice: 2.50,
    salePrice: 3.75,
    imageUrl: '/assets/images/milk.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 102,
    itemNumber: 'SKU-BREAD-001',
    description: 'White Sliced Bread 800g',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Bakery',
    costPrice: 1.20,
    salePrice: 1.80,
    imageUrl: '/assets/images/bread.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 103,
    itemNumber: 'SKU-COFFEE-001',
    description: 'Premium Coffee Beans 1kg',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Coffee',
    costPrice: 18.50,
    salePrice: 27.75,
    imageUrl: '/assets/images/coffee.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 104,
    itemNumber: 'SKU-SUGAR-001',
    description: 'Granulated Sugar 1kg',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Dry Goods',
    costPrice: 0.85,
    salePrice: 1.25,
    imageUrl: '/assets/images/sugar.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 105,
    itemNumber: 'SKU-ICECREAM-001',
    description: 'Vanilla Ice Cream 2L',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Frozen',
    costPrice: 4.50,
    salePrice: 6.75,
    imageUrl: '/assets/images/icecream.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 106,
    itemNumber: 'SKU-PASTA-001',
    description: 'Penne Pasta 500g',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Dry Goods',
    costPrice: 1.30,
    salePrice: 1.95,
    imageUrl: '/assets/images/pasta.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 107,
    itemNumber: 'SKU-FRIES-001',
    description: 'French Fries 1kg',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Frozen',
    costPrice: 2.50,
    salePrice: 3.75,
    imageUrl: '/assets/images/fries.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 108,
    itemNumber: 'SKU-EGGS-001',
    description: 'Free Range Eggs 12pk',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Dairy',
    costPrice: 3.20,
    salePrice: 4.80,
    imageUrl: '/assets/images/eggs.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 109,
    itemNumber: 'SKU-WINE-001',
    description: 'Red Wine Merlot 750ml',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Alcohol',
    costPrice: 12.50,
    salePrice: 18.75,
    imageUrl: '/assets/images/wine.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 110,
    itemNumber: 'SKU-BEER-001',
    description: 'Lager Beer 330ml 24pk',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Alcohol',
    costPrice: 3.85,
    salePrice: 5.75,
    imageUrl: '/assets/images/beer.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 111,
    itemNumber: 'SKU-YOGURT-001',
    description: 'Greek Yogurt 500g',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Dairy',
    costPrice: 2.40,
    salePrice: 3.60,
    imageUrl: '/assets/images/yogurt.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 112,
    itemNumber: 'SKU-CLEANER-001',
    description: 'Multi-Purpose Cleaner 5L',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Non-Food',
    costPrice: 8.00,
    salePrice: 12.00,
    imageUrl: '/assets/images/cleaner.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 113,
    itemNumber: 'SKU-PAPER-001',
    description: 'Paper Towels Roll 12pk',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Non-Food',
    costPrice: 3.00,
    salePrice: 4.50,
    imageUrl: '/assets/images/paper.jpg',
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  // ---- Vapiano Items (franchiseId: 10) ----
  {
    id: 1001,
    itemNumber: 'VAP-FLOUR-001',
    description: '00 Flour (Caputo) 25kg',
    uom: 'Bag',
    status: 'Active',
    primaryCategory: 'Flour & Dry Goods',
    costPrice: 18.50,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1002,
    itemNumber: 'VAP-EGGS-001',
    description: 'Free Range Eggs (Large) 360pk',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Fresh Dairy',
    costPrice: 42.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1003,
    itemNumber: 'VAP-SEMOLINA-001',
    description: 'Semolina Flour (Fine) 25kg',
    uom: 'Bag',
    status: 'Active',
    primaryCategory: 'Flour & Dry Goods',
    costPrice: 22.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1004,
    itemNumber: 'VAP-GUANCIALE-001',
    description: 'Guanciale (Italian Cured Pork Cheek) 2kg',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Italian Charcuterie',
    costPrice: 28.50,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1005,
    itemNumber: 'VAP-PECORINO-001',
    description: 'Pecorino Romano DOP 5kg wheel',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Italian Cheese',
    costPrice: 65.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1006,
    itemNumber: 'VAP-MOZZ-001',
    description: 'Fior di Latte Mozzarella 1kg',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Italian Cheese',
    costPrice: 12.80,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1007,
    itemNumber: 'VAP-TOMATO-001',
    description: 'San Marzano Tomatoes DOP 2.5kg tin',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Tinned Goods',
    costPrice: 4.80,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1008,
    itemNumber: 'VAP-OLIVE-001',
    description: 'Extra Virgin Olive Oil 5L',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Oils & Vinegars',
    costPrice: 24.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1009,
    itemNumber: 'VAP-BASIL-001',
    description: 'Fresh Basil (Living Pot)',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Fresh Herbs',
    costPrice: 1.80,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1010,
    itemNumber: 'VAP-PROSECCO-001',
    description: 'Prosecco DOC 750ml',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Wines & Spirits',
    costPrice: 6.50,
    salePrice: 9.50,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: false,
  },
  {
    id: 1011,
    itemNumber: 'VAP-APEROL-001',
    description: 'Aperol 700ml',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Wines & Spirits',
    costPrice: 14.50,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1012,
    itemNumber: 'VAP-PRAWNS-001',
    description: 'King Prawns (Raw, Peeled) 1kg',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Fresh Seafood',
    costPrice: 18.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1013,
    itemNumber: 'VAP-PESTO-001',
    description: 'Fresh Basil Pesto 1kg',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Sauces & Condiments',
    costPrice: 8.50,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 1014,
    itemNumber: 'VAP-CARBONARA-SALE',
    description: 'Spaghetti Carbonara',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Pasta Dishes',
    costPrice: 3.20,
    salePrice: 13.95,
    imageUrl: undefined,
    isStockItem: false,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: true,
  },
  {
    id: 1015,
    itemNumber: 'VAP-MARGHERITA-SALE',
    description: 'Pizza Margherita',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Pizza',
    costPrice: 2.80,
    salePrice: 11.95,
    imageUrl: undefined,
    isStockItem: false,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: true,
  },
  {
    id: 1016,
    itemNumber: 'VAP-SPRITZ-SALE',
    description: 'Aperol Spritz',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Bar Drinks',
    costPrice: 2.10,
    salePrice: 9.50,
    imageUrl: undefined,
    isStockItem: false,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: true,
  },
  {
    id: 1017,
    itemNumber: 'VAP-GAMBERONI-SALE',
    description: 'Spaghetti Gamberoni',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Pasta Dishes',
    costPrice: 4.50,
    salePrice: 15.95,
    imageUrl: undefined,
    isStockItem: false,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: true,
  },
  {
    id: 1018,
    itemNumber: 'VAP-PARMIGIANO-001',
    description: 'Parmigiano Reggiano DOP 1kg',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Italian Cheese',
    costPrice: 22.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  // ---- Five Guys Items (franchiseId: 11) ----
  {
    id: 2001,
    itemNumber: 'FG-BEEF-001',
    description: 'Fresh Ground Beef Patties (never frozen) 4oz x48',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Fresh Beef',
    costPrice: 68.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2002,
    itemNumber: 'FG-POTATO-001',
    description: 'Fresh Potatoes (Maris Piper) 25kg sack',
    uom: 'Bag',
    status: 'Active',
    primaryCategory: 'Fresh Produce',
    costPrice: 8.50,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2003,
    itemNumber: 'FG-PEANUT-OIL-001',
    description: '100% Refined Peanut Oil 20L',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Oils',
    costPrice: 32.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2004,
    itemNumber: 'FG-BUNS-001',
    description: 'Sesame Seed Burger Buns x60',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Bakery',
    costPrice: 18.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2005,
    itemNumber: 'FG-CHEESE-001',
    description: 'American Cheese Slices x160',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Dairy',
    costPrice: 22.50,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2006,
    itemNumber: 'FG-LETTUCE-001',
    description: 'Iceberg Lettuce (whole heads) x12',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Fresh Produce',
    costPrice: 9.60,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2007,
    itemNumber: 'FG-TOMATO-001',
    description: 'Fresh Tomatoes (Vine) 5kg',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Fresh Produce',
    costPrice: 7.50,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2008,
    itemNumber: 'FG-ONION-001',
    description: 'White Onions 10kg',
    uom: 'Bag',
    status: 'Active',
    primaryCategory: 'Fresh Produce',
    costPrice: 5.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2009,
    itemNumber: 'FG-JALAPENO-001',
    description: 'Fresh Jalapeños 2kg',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Fresh Produce',
    costPrice: 6.80,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2010,
    itemNumber: 'FG-MUSHROOM-001',
    description: 'Button Mushrooms (Sliced) 2kg',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Fresh Produce',
    costPrice: 5.40,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2011,
    itemNumber: 'FG-PEPPER-001',
    description: 'Green Peppers 5kg',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Fresh Produce',
    costPrice: 6.20,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2012,
    itemNumber: 'FG-BACON-001',
    description: 'Smoked Back Bacon 2.5kg',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Fresh Meat',
    costPrice: 14.50,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2013,
    itemNumber: 'FG-HOTDOG-001',
    description: 'All-Beef Hot Dog Franks x48',
    uom: 'Case',
    status: 'Active',
    primaryCategory: 'Fresh Meat',
    costPrice: 24.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2014,
    itemNumber: 'FG-PEANUTS-001',
    description: 'Roasted Peanuts (In-Shell) 10kg',
    uom: 'Bag',
    status: 'Active',
    primaryCategory: 'Snacks',
    costPrice: 12.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2015,
    itemNumber: 'FG-SHAKE-BASE-001',
    description: 'Vanilla Milkshake Base 10L',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Milkshake',
    costPrice: 15.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2016,
    itemNumber: 'FG-CAJUN-001',
    description: 'Five Guys Cajun Seasoning 2kg',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Seasonings',
    costPrice: 8.50,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
  {
    id: 2017,
    itemNumber: 'FG-CHEESEBURGER-SALE',
    description: 'Cheeseburger',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Burgers',
    costPrice: 3.40,
    salePrice: 11.75,
    imageUrl: undefined,
    isStockItem: false,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: true,
  },
  {
    id: 2018,
    itemNumber: 'FG-BACONBURGER-SALE',
    description: 'Bacon Cheeseburger',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Burgers',
    costPrice: 4.10,
    salePrice: 13.25,
    imageUrl: undefined,
    isStockItem: false,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: true,
  },
  {
    id: 2019,
    itemNumber: 'FG-FRIES-REG-SALE',
    description: 'Five Guys Fries (Regular)',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Fries',
    costPrice: 1.20,
    salePrice: 5.25,
    imageUrl: undefined,
    isStockItem: false,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: true,
  },
  {
    id: 2020,
    itemNumber: 'FG-CAJUN-FRIES-SALE',
    description: 'Cajun Fries (Regular)',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Fries',
    costPrice: 1.30,
    salePrice: 5.25,
    imageUrl: undefined,
    isStockItem: false,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: true,
  },
  {
    id: 2021,
    itemNumber: 'FG-HOTDOG-SALE',
    description: 'All-Beef Hot Dog',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Hot Dogs',
    costPrice: 2.20,
    salePrice: 8.75,
    imageUrl: undefined,
    isStockItem: false,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: true,
  },
  {
    id: 2022,
    itemNumber: 'FG-SHAKE-SALE',
    description: 'Milkshake (Regular)',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Milkshakes',
    costPrice: 1.80,
    salePrice: 6.75,
    imageUrl: undefined,
    isStockItem: false,
    isSaleItem: true,
    isPosEnabled: true,
    isMadeInStore: true,
  },
  {
    id: 2023,
    itemNumber: 'FG-CHEESE-SAUCE-001',
    description: 'Cheese Sauce Mix 5kg',
    uom: 'Each',
    status: 'Active',
    primaryCategory: 'Sauces',
    costPrice: 11.00,
    salePrice: 0,
    imageUrl: undefined,
    isStockItem: true,
    isSaleItem: false,
    isPosEnabled: false,
    isMadeInStore: false,
  },
];

const mockUoms = [
  { uom: 'Each', description: 'Each', conversionFactor: 1 },
  { uom: 'Case', description: 'Case of 12', conversionFactor: 12 },
  { uom: 'Pallet', description: 'Pallet of 48 cases', conversionFactor: 576 },
];

/* ---- Mocked Client Implementation ---- */

import { IItemsClient } from '../../interfaces';
export class ItemsClientMocked implements IItemsClient {
  constructor(baseUrl?: string) {
    console.log('[ItemsClientMocked] Initialized with', mockItems.length, 'mock items');
  }

  async getUoms(
    itemNumber: string,
    storeNumber?: string,
    withoutWeight?: boolean,
    withoutLiquid?: boolean,
    cancelToken?: CancelToken
  ): Promise<SuccessResponse_1OfGetUomsResponse> {
    await delay();
    return successResponse({ uoms: mockUoms }) as SuccessResponse_1OfGetUomsResponse;
  }

  async getByItemNumber(itemNumber: string, storeNumber?: string, cancelToken?: CancelToken): Promise<SuccessResponse_1OfItem> {
    await delay();
    console.log('[ItemsClientMocked] getByItemNumber called with:', itemNumber, 'for store:', storeNumber);
    const item = mockItems.find((i) => i.itemNumber === itemNumber);
    console.log('[ItemsClientMocked] Found item:', item?.description, 'with itemNumber:', item?.itemNumber);
    return successResponse(item) as SuccessResponse_1OfItem;
  }

  async get(
    search?: string,
    storeNumber?: string,
    hasRecentActivity?: boolean,
    isMadeInStore?: boolean,
    excludeRecipes?: boolean,
    isStockItem?: boolean,
    isSaleItem?: boolean,
    isProduct?: boolean,
    isMaterial?: boolean,
    isDailyWaste?: boolean,
    sort?: string,
    direction?: string,
    skip?: number,
    take?: number,
    addItems?: boolean,
    cancelToken?: CancelToken
  ): Promise<SuccessResponse_1OfGetItemsResponse> {
    await delay();
    console.log('[ItemsClientMocked] get called with filters:', {
      search, storeNumber, isStockItem, isSaleItem, isMadeInStore, skip, take
    });
    
    let filtered = filterItemsByStore(mockItems, storeNumber);

    if (search) {
      filtered = filtered.filter(
        (i) =>
          i.itemNumber?.toLowerCase().includes(search.toLowerCase()) ||
          i.description?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (isStockItem !== undefined) {
      filtered = filtered.filter((i) => i.isStockItem === isStockItem);
    }
    if (isSaleItem !== undefined) {
      filtered = filtered.filter((i) => i.isSaleItem === isSaleItem);
    }
    if (isMadeInStore !== undefined) {
      filtered = filtered.filter((i) => i.isMadeInStore === isMadeInStore);
    }

    // Sorting
    if (sort) {
      filtered.sort((a, b) => {
        const aVal = (a as any)[sort];
        const bVal = (b as any)[sort];
        if (aVal === bVal) return 0;
        const comparison = aVal > bVal ? 1 : -1;
        return direction === 'desc' ? -comparison : comparison;
      });
    }

    const start = skip || 0;
    const end = start + (take || 10);
    const paginatedResults = filtered.slice(start, end);

    console.log('[ItemsClientMocked] Returning', paginatedResults.length, 'of', filtered.length, 'items');

    return successResponse({
      items: paginatedResults,
      itemCount: filtered.length,
    }) as SuccessResponse_1OfGetItemsResponse;
  }

  async getByStoreNumbers(body?: GetItemsByStoresRequest, cancelToken?: CancelToken): Promise<SuccessResponse_1OfList_1OfItem> {
    await delay();
    const stores = body?.stores || [];
    const storeNumber = stores.length > 0 ? stores[0] : undefined;
    const filtered = filterItemsByStore(mockItems, storeNumber);
    return successResponse(filtered) as SuccessResponse_1OfList_1OfItem;
  }

  async getByIds(body?: GetItemsByIdsRequest, cancelToken?: CancelToken): Promise<SuccessResponse_1OfList_1OfItem> {
    await delay();
    const itemNums = body?.itemNumbers || [];
    const items = mockItems.filter((i) => itemNums.includes(i.itemNumber!));
    return successResponse(items) as SuccessResponse_1OfList_1OfItem;
  }

  async put(itemNumber: string, body?: Item, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const item = mockItems.find((i) => i.itemNumber === itemNumber);
    if (item && body) {
      Object.assign(item, body);
    }
  }

  async patch(itemNumber: string, body?: Item, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const item = mockItems.find((i) => i.itemNumber === itemNumber);
    if (item && body) {
      Object.assign(item, body);
    }
  }

  // ====== Auto-generated stub methods ======































    async getStockItems(storeNumber: string, cancelToken?: CancelToken | undefined): Promise<SuccessResponse_1OfList_1OfItem> {
        throw new Error('Method getStockItems not implemented in mocked client');
    }

    async getTransferItems(search?: string | undefined, from?: string | undefined, to?: string | undefined, cancelToken?: CancelToken | undefined): Promise<SuccessResponse_1OfGetItemsResponse> {
        throw new Error('Method getTransferItems not implemented in mocked client');
    }

    async addStoreFeedChanges(storeNumber: string, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method addStoreFeedChanges not implemented in mocked client');
    }

    async addFeedChanges(body?: RefreshItemsRequest | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method addFeedChanges not implemented in mocked client');
    }

    async applyChange(itemFeedChangeId: number, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method applyChange not implemented in mocked client');
    }

    async disregardChange(itemFeedChangeId: number, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method disregardChange not implemented in mocked client');
    }

    async applyChanges(body?: number[] | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method applyChanges not implemented in mocked client');
    }

    async disregardChanges(body?: number[] | undefined, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method disregardChanges not implemented in mocked client');
    }

    async getRecipeChanges(search?: string | undefined, storeNumber?: string | undefined, skip?: number | undefined, take?: number | undefined, isNew?: boolean | undefined, applied?: boolean | undefined, disregarded?: boolean | undefined, cancelToken?: CancelToken | undefined): Promise<PagedData_1OfItemFeedChange> {
        throw new Error('Method getRecipeChanges not implemented in mocked client');
    }

    async getFranchiseRecipeChanges(franchiseName?: string | undefined, skip?: number | undefined, take?: number | undefined, cancelToken?: CancelToken | undefined): Promise<PagedData_1OfGroupedItemFeedChange> {
        throw new Error('Method getFranchiseRecipeChanges not implemented in mocked client');
    }

    async getAllStoresRecipeChanges(skip?: number | undefined, take?: number | undefined, cancelToken?: CancelToken | undefined): Promise<PagedData_1OfGroupedItemFeedChange> {
        throw new Error('Method getAllStoresRecipeChanges not implemented in mocked client');
    }

    async getItemAuditsByItemNumber(itemNumber: string, cancelToken?: CancelToken | undefined): Promise<SuccessResponse_1OfList_1OfItemAudit> {
        throw new Error('Method getItemAuditsByItemNumber not implemented in mocked client');
    }

    async getChangeDetails(itemFeedChangeId: number, cancelToken?: CancelToken | undefined): Promise<ItemCompare> {
        throw new Error('Method getChangeDetails not implemented in mocked client');
    }

    async getRecipeChangesById(body?: GetRecipeChangesByIDRequest | undefined, cancelToken?: CancelToken | undefined): Promise<ItemFeedChange[]> {
        throw new Error('Method getRecipeChangesById not implemented in mocked client');
    }

    async updateStoreItemOrderTypes(storeNumber: string, cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method updateStoreItemOrderTypes not implemented in mocked client');
    }

    async updateItemOrderTypes(cancelToken?: CancelToken | undefined): Promise<void> {
        throw new Error('Method updateItemOrderTypes not implemented in mocked client');
    }

    async itemStockOnHand(body?: ItemStockOnHandRequest | undefined, cancelToken?: CancelToken | undefined): Promise<ItemStockOnHandResponse[]> {
        throw new Error('Method itemStockOnHand not implemented in mocked client');
    }

}
